"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Code as Code2, Gauge, Search, Smartphone, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, Terminal, Activity, Zap, Cpu, FileCode2, Layers } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const technicalPillars = [{
  id: "crawl",
  title: "Crawlability & Indexing",
  description: "I eliminate crawl budget waste, render-blocking scripts, and canonical conflicts to help search engines discover, crawl and index important pages more efficiently.",
  metric: "Reduced crawl waste",
  icon: "Search"
}, {
  id: "speed",
  title: "Core Web Vitals Optimization",
  description: "I rewrite performance bottlenecks to achieve faster load times and more stable interactivity that support search performance and user experience.",
  metric: "Performance-focused optimisation",
  icon: "Gauge"
}, {
  id: "mobile",
  title: "Mobile-First & JS SEO",
  description: "I audit client-side JavaScript rendering and hydrations, ensuring Googlebot sees full HTML content on mobile viewports.",
  metric: "Improved technical stability",
  icon: "Smartphone"
}, {
  id: "schema",
  title: "Structured Data & AI Search",
  description: "I implement precise JSON-LD schema architecture, preparing your business for both classic Google Search and modern AI engines.",
  metric: "Validated structured data",
  icon: "Code2"
}];
const liveDiagnostics = [{
  label: "Core Web Vitals",
  value: "Audited",
  status: "Performance-focused optimisation",
  color: "text-chart-4"
}, {
  label: "Crawl Budget Waste",
  value: "Minimised",
  status: "Reduced crawl waste",
  color: "text-chart-4"
}, {
  label: "Render-Blocking Scripts",
  value: "Resolved",
  status: "Improved technical stability",
  color: "text-chart-4"
}, {
  label: "Schema Validation",
  value: "Valid",
  status: "Validated structured data",
  color: "text-chart-4"
}];
export default function Technicalfoundation() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<number>(0);
  const getPillarIcon = (iconName: string) => {
    switch (iconName) {
      case "Search":
        return <Search className="size-5 text-primary" />;
      case "Gauge":
        return <Gauge className="size-5 text-primary" />;
      case "Smartphone":
        return <Smartphone className="size-5 text-primary" />;
      case "Code2":
        return <Code2 className="size-5 text-primary" />;
      default:
        return <Activity className="size-5 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="technicalfoundationhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 text-foreground">
      {/* Background Glows */}
      <div className="pointer-events-none absolute -top-40 -right-40 size-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 size-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Hero Content */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3.5 py-1.5 text-xs font-semibold text-primary mb-6">
              <ShieldCheck className="size-4" />
              <span>Technical SEO Architecture & Optimization</span>
            </div>

            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl lg:leading-tight">
              Fix the Technical Foundation of Your SEO
            </h2>

            <p className="mt-6 text-base text-muted-foreground md:text-lg leading-relaxed">
              {"I audit and rebuild your website's technical architecture to help search engines discover, crawl and index important pages more efficiently, with faster load times for your users. When you hire YE Consultancy, you work directly with me—Yassine Essaadi—ensuring direct communication and hands-on technical execution."}
            </p>

            {/* Insight Callout Card */}
            <div className="mt-8 rounded-xl border border-border bg-card p-4 sm:p-5 shadow-xs">
              <div className="flex items-start gap-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                  <Zap className="size-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-foreground">53% of mobile visits</span>
                    <Badge variant="secondary" className="bg-secondary text-foreground text-xs font-medium">
                      Proven Search Insight
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    are abandoned if pages take longer than 3 seconds to load. Technical health directly influences user engagement and search visibility.
                  </p>
                </div>
              </div>
            </div>

            {/* Action CTA Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Link to="/ContactPage">
                <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-6 text-base rounded-lg shadow-md gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
              <Link to="/CaseStudiesPage">
                <Button variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-accent hover:text-accent-foreground font-medium px-6 py-6 text-base rounded-lg">
                  Explore My Case Studies
                </Button>
              </Link>
            </div>

            {/* Micro Trust Proof Bar */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 pt-6 border-t border-border text-xs text-muted-foreground font-medium">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Hands-On Technical Execution</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Bespoke Technical Audits</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Actionable Code Specs</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Live Interactive Audit Terminal Mockup */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-xl">
              {/* Terminal Window Header */}
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-2">
                  <div className="size-3 rounded-full bg-destructive/70" />
                  <div className="size-3 rounded-full bg-chart-5/70" />
                  <div className="size-3 rounded-full bg-chart-4/70" />
                  <span className="ml-2 font-mono text-xs text-muted-foreground">yassine-seo-audit.sh</span>
                </div>
                <Badge variant="outline" className="font-mono text-xs border-primary/30 text-primary bg-primary/5">
                  Live Technical Audit
                </Badge>
              </div>

              {/* Terminal Execution Body */}
              <div className="space-y-4 font-mono text-xs">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Terminal className="size-4 text-primary shrink-0" />
                  <span>{"$ ye-consultancy analyze --target=your-site.com"}</span>
                </div>

                <div className="rounded-lg bg-background p-4 border border-border space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <Cpu className="size-3.5 text-primary" /> Architecture Status
                    </span>
                    <span className="font-semibold text-chart-4">PASS</span>
                  </div>
                  <Separator className="bg-border" />
                  
                  {liveDiagnostics.map((metric, i) => <div key={i} data-index={i} className="flex items-center justify-between text-xs">
                      <span className="text-foreground">{metric.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-foreground">{metric.value}</span>
                        <span className={`text-[11.5px] font-semibold px-1.5 py-0.5 rounded bg-muted ${metric.color}`}>
                          {metric.status}
                        </span>
                      </div>
                    </div>)}
                </div>

                {/* Audit Pillar Selector */}
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-sans text-muted-foreground font-semibold">
                    My Technical Audit Modules:
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {technicalPillars.map((pillar, idx) => <Button key={pillar.id} data-index={idx} variant={activeTab === idx ? "default" : "outline"} size="sm" onClick={() => setActiveTab(idx)} className={`justify-start text-xs h-9 rounded-md transition-all ${activeTab === idx ? "bg-primary text-primary-foreground shadow-xs" : "bg-background text-foreground hover:bg-muted border-border"}`}>
                        <FileCode2 className="size-3.5 shrink-0 mr-1" />
                        <span className="truncate">{pillar.title.split("&")[0]}</span>
                      </Button>)}
                  </div>
                </div>

                {/* Active Diagnostic Detail */}
                <div className="rounded-lg bg-muted/60 p-3.5 border border-border/60 text-xs font-sans">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-semibold text-foreground flex items-center gap-1.5">
                      <Layers className="size-3.5 text-primary" />
                      {technicalPillars[activeTab].title}
                    </span>
                    <Badge variant="secondary" className="text-[11.5px] bg-primary/10 text-primary border-transparent font-mono">
                      {technicalPillars[activeTab].metric}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* 4 Pillars Summary Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="mt-16 md:mt-20 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {technicalPillars.map((pillar, i) => <motion.div key={pillar.id} data-index={i} variants={itemVariants} className="group relative rounded-xl border border-border bg-card p-6 shadow-xs hover:shadow-md transition-all duration-200">
              <div className="mb-4 inline-flex size-10 items-center justify-center rounded-lg bg-secondary">
                {getPillarIcon(pillar.icon)}
              </div>

              <h3 className="font-serif text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                {pillar.title}
              </h3>

              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {pillar.description}
              </p>

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground font-mono">{pillar.metric}</span>
                <Link to="/ServicesPage" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
                  Learn more <ArrowRight className="size-3" />
                </Link>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}